import { useState } from "react";
import { sendFile } from "@/common/services/hold-document";
import { updateReturnOfDeposit, getAllReturnOfDeposits } from "@/common/services/devolution-deposit.api";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { IDevolutionDeposit } from "@/types/devolution-deposit";

interface FileUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRow: IDevolutionDeposit | null;
  setDevolutionDeposits: React.Dispatch<React.SetStateAction<IDevolutionDeposit[]>>;
  renterId: string;
}

export default function FileUploadModal({
  isOpen,
  onClose,
  selectedRow,
  setDevolutionDeposits,
  renterId
}: FileUploadModalProps) {
  const [dragging, setDragging] = useState(false);
  const { notifyError, notifySuccess } = useToastContext();


  const handleUpload = async (file: File) => {
    if (!selectedRow) return;
    try {
      const uploadedFiles = await sendFile({
        prefix: `customers/${renterId}/devolution-deposit`,
        file,
        name: `comprovante_${selectedRow.renterId}`,
      });

      const fileKey = uploadedFiles.data[0]?.key;
      if (!fileKey) throw new Error("Key não retornada pelo servidor");


      const result = await updateReturnOfDeposit(renterId, fileKey);
      if (!result.ok) {
        notifyError("Erro ao anexar arquivo");
        return;
      }

      notifySuccess(`Arquivo "${file.name}" anexado com sucesso!`);

      const response = await getAllReturnOfDeposits();
      const data: IDevolutionDeposit[] = response.data ?? response.data;

      const formattedData = data.map((item: IDevolutionDeposit) => {
        const dateLocacao = new Date(item.dateLocation);
        const dateDevolucao = new Date(item.dateDevolution);

        const contactDate = new Date(dateDevolucao);
        contactDate.setDate(contactDate.getDate() + 45);

        return {
          ...item,
          date_locacao: dateLocacao.toLocaleDateString("pt-BR"),
          date_devolucao: dateDevolucao.toLocaleDateString("pt-BR"),
          contactDate: contactDate.toLocaleDateString("pt-BR"),
          file: item.file ? (
            <a
              href={`https://assets.locavibe.com.br/${item.file}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              {item.file.split("/").reverse()[0]}
            </a>
          ) : (
            <span>Sem arquivo</span>
          ),
        };
      });

      setDevolutionDeposits(formattedData as unknown as IDevolutionDeposit[]);
    } catch (err) {
      notifyError("Erro ao anexar arquivo");
      console.error(err);
    }
    onClose();
  };


  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files[0]);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-2xl shadow-lg w-[400px]">
        <h2 className="text-lg font-semibold mb-4">Anexar Arquivo</h2>

        <div
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setDragging(true)}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition ${dragging ? "border-blue-500 bg-blue-50" : "border-gray-300"
            }`}
        >
          <p className="text-gray-500">Arraste o arquivo aqui</p>
          <p className="text-sm text-gray-400 mt-2">ou clique para selecionar</p>
          <input
            type="file"
            className="hidden"
            id="fileInput"
            onChange={(e) => e.target.files && handleUpload(e.target.files[0])}
          />
          <label
            htmlFor="fileInput"
            className="mt-3 inline-block bg-locavibe text-white px-4 py-2 rounded-lg cursor-pointer hover:bg-red-700"
          >
            Selecionar Arquivo
          </label>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100"
            onClick={onClose}
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}
